/* RESPONSIBLE TEAM: team-frontend-tech */
/* eslint-disable promise/prefer-await-to-then */
import Modifier from 'ember-modifier';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import { registerDestructor } from '@ember/destroyable';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import ENV from 'embercom/config/environment';
import { isReactRoute } from 'embercom/lib/react-route';
import { loadScript, loadStylesheet } from 'embercom/lib/dom-utils';
import { getTeammateAppUrls } from 'embercom/lib/react-islands';
import { captureException } from 'embercom/lib/sentry';

const CONTAINER_ID = 'react-teammate-app-island-container';

interface Args {
  Positional: [];
  Named: { onAppReady: () => void; onError: (error: Error) => void };
}

interface RenderReactTeammateAppIslandSignature {
  Element: Element;
  Args: Args;
}

class RenderReactTeammateAppIsland extends Modifier<RenderReactTeammateAppIslandSignature> {
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare router: RouterService;

  runCount: number;

  unmount?: Function;

  constructor(owner: any, args: any) {
    super(owner, args);
    this.runCount = 0;

    registerDestructor(this, this.cleanup);
  }

  modify(element: Element, _props: Args['Positional'], { onAppReady, onError }: Args['Named']) {
    this.runCount++;
    this.renderReactApp(element)
      .then(() => {
        onAppReady?.();
      })
      .catch((error) => {
        console.error(error);
        onError?.(error);
      });
  }

  cleanup = () => {
    this.unmount?.();
  };

  async renderReactApp(element: Element) {
    if (ENV.environment === 'test') {
      return;
    }

    if (ENV.environment === 'development') {
      if (!this.reactTeammateAppIsland.loadedViteScripts) {
        await Promise.all([
          loadScript('http://embercom.test:4200/a/react-refresh.js'),
          loadScript('http://localhost:5173/r/@vite/client'),
        ]);

        this.reactTeammateAppIsland.loadedViteScripts = true;
      }
    }

    let { jsUrl, cssUrl } = getTeammateAppUrls();

    if (!this.reactTeammateAppIsland.loadedEntryPointScript) {
      await loadScript(jsUrl);

      this.reactTeammateAppIsland.loadedEntryPointScript = true;
    }

    if (!window.ReactTeammateApp) {
      throw new Error('ReactTeammateApp not found after loading script');
    }

    let reactAppMountElement = document.createElement('div');
    reactAppMountElement.id = CONTAINER_ID;
    reactAppMountElement.className = 'h-full w-full';

    let shadowRoot;
    try {
      shadowRoot = element.attachShadow({ mode: 'open' });
    } catch (error) {
      let reactContainer = element.shadowRoot?.querySelector(`#${CONTAINER_ID}`);
      let hasReactContent = reactContainer && reactContainer.children.length > 0;

      captureException(error, {
        fingerprint: ['react-teammate-app-island', 'attachshadow-failed'],
        extra: {
          containerId: CONTAINER_ID,
          childrenCount: reactContainer?.children.length || 0,
          shadowRootExists: !!element.shadowRoot,
          hasReactContent,
          hasContainer: !!reactContainer,
          errorMessage: error instanceof Error ? error.message : String(error),
          modifierRunCount: this.runCount,
        },
        tags: {
          responsibleTeam: 'team-frontend-tech',
          responsible_team: 'team-frontend-tech',
        },
      });
      throw error;
    }

    await loadStylesheet(cssUrl, shadowRoot);
    shadowRoot.appendChild(reactAppMountElement);

    this.unmount = window.ReactTeammateApp.render(reactAppMountElement, {
      currentPath: this.reactPath,
    });
  }

  get reactPath() {
    return isReactRoute(this.router.currentRoute) ? this.router.currentURL : '/empty';
  }
}

export default RenderReactTeammateAppIsland;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'render-react-teammate-app-island': typeof RenderReactTeammateAppIsland;
  }
}
